/**
 * The subject + media-type pairs that may participate in offer bundling.
 *
 * Values are backend enum names (what arrives on `offer.subject` / `offer.mediaType` and on
 * `certification.subjectId` / `certification.mediaTypeId`) -- never the labels shown in the ATMS UI.
 * A UI label here would silently never match.
 *
 * Which list is in effect is chosen by the `offerBundlingExpansion` feature flag, resolved
 * per-linguist in ATMSAssignment and delivered on the offer-list response. Flag off, absent, or
 * unresolvable means the base list, which is today's behavior.
 */

const freezePairs = pairs => Object.freeze(pairs.map(Object.freeze));

/** Today's single allowed pair. The flag-off behavior, byte-identical to pre-expansion bundling. */
export const BASE_ALLOWED_PAIRS = freezePairs([{ subject: 'GENERAL', mediaType: 'SOFTWARE' }]);

/**
 * The base list plus the pairs TSOps approved for expansion, sorted by subject then media type.
 *
 * These are the enum names from ATMSWeb's `Subject` and `MediaType`, resolved from the UI labels
 * TSOps supplied. Six do not transform mechanically from their label and are the likely source of
 * a silent never-match if edited by hand: `BUSINESS` is "General Business / Commerce",
 * `INTERNET` is "Internet, E-Commerce", `CORRESPONDENCE` is "Private Correspondence / Letters",
 * `TELECOM` is "Telecommunications", `PRODUCT_DESC` is "Product Description", and `TEXT` is
 * "Document / Text". Note also that `MARKETING_PUBLIC_RELATIONS` ("Marketing & Advertising
 * material / Public Relations") is a different subject from `MARKETING_TRANSCREATION`.
 */
export const EXPANDED_ALLOWED_PAIRS = freezePairs([
  ...BASE_ALLOWED_PAIRS,
  { subject: 'ACCOUNTING', mediaType: 'SOFTWARE' },
  { subject: 'BUSINESS', mediaType: 'CERTIFICATES' },
  { subject: 'BUSINESS', mediaType: 'PRODUCT_DESC' },
  { subject: 'BUSINESS', mediaType: 'PRODUCT_METADATA' },
  { subject: 'BUSINESS', mediaType: 'SOFTWARE' },
  { subject: 'BUSINESS', mediaType: 'TEXT' },
  { subject: 'CHEMICAL', mediaType: 'WEBSITE' },
  { subject: 'CORRESPONDENCE', mediaType: 'TEXT' },
  { subject: 'ELECTRONICS', mediaType: 'SOFTWARE' },
  { subject: 'ELECTRONICS', mediaType: 'WEBSITE' },
  { subject: 'GAMES', mediaType: 'SOFTWARE' },
  { subject: 'GAMES', mediaType: 'WEBSITE' },
  { subject: 'GENERAL', mediaType: 'PRODUCT_DESC' },
  { subject: 'GENERAL', mediaType: 'TEXT' },
  { subject: 'GENERAL', mediaType: 'WEBSITE' },
  { subject: 'HUMAN_RESOURCES', mediaType: 'CERTIFICATES' },
  { subject: 'HUMAN_RESOURCES', mediaType: 'SOFTWARE' },
  { subject: 'HUMAN_RESOURCES', mediaType: 'TEXT' },
  { subject: 'HUMAN_RESOURCES', mediaType: 'WEBSITE' },
  { subject: 'INFORMATION_TECHNOLOGY', mediaType: 'WEBSITE' },
  { subject: 'INTERNET', mediaType: 'CERTIFICATES' },
  { subject: 'INTERNET', mediaType: 'SOFTWARE' },
  { subject: 'INTERNET', mediaType: 'WEBSITE' },
  { subject: 'LEGAL', mediaType: 'CERTIFICATES' },
  { subject: 'LEGAL', mediaType: 'WEBSITE' },
  { subject: 'MARKETING_PUBLIC_RELATIONS', mediaType: 'TEXT' },
  { subject: 'MARKETING_PUBLIC_RELATIONS', mediaType: 'WEBSITE' },
  { subject: 'MECHANICAL', mediaType: 'CERTIFICATES' },
  { subject: 'MUSIC', mediaType: 'BOOKS' },
  { subject: 'SCIENCE', mediaType: 'WEBSITE' },
  { subject: 'TELECOM', mediaType: 'SOFTWARE' },
]);

const pairKey = (subject, mediaType) => `${subject}|${mediaType}`;

const toKeySet = pairs => new Set(pairs.map(pair => pairKey(pair.subject, pair.mediaType)));

const BASE_KEYS = toKeySet(BASE_ALLOWED_PAIRS);
const EXPANDED_KEYS = toKeySet(EXPANDED_ALLOWED_PAIRS);

/**
 * Whether a subject + media-type pair is in the effective allowlist.
 *
 * Matching is exact and case-sensitive, matching the hardcoded equality checks this replaces.
 * A null or undefined subject or media type is never allowed.
 *
 * @param {string} subject backend subject enum name
 * @param {string} mediaType backend media-type enum name
 * @param {boolean} expansionEnabled resolved value of the `offerBundlingExpansion` flag
 */
export const isPairAllowed = (subject, mediaType, expansionEnabled) => {
  if (subject == null || mediaType == null) {
    return false;
  }
  const allowedKeys = expansionEnabled === true ? EXPANDED_KEYS : BASE_KEYS;
  return allowedKeys.has(pairKey(subject, mediaType));
};

/**
 * Slices to carve out of the effective allowlist, so a client, skill or language arc can opt out
 * of bundling without removing the whole subject + media-type pair.
 *
 * Each row is a partial pattern over the fields the grouping key is built from. An omitted field
 * matches anything, so a row narrows only on the fields it names, and an offer is excluded when it
 * matches every field one row does name. `subject` and `mediaType` are required: an exclusion
 * always names the pair it carves out of, which also stops an empty row from excluding everything.
 *
 *   { subject: 'GENERAL', mediaType: 'WEBSITE' }
 *     -> that whole pair stops bundling
 *   { subject: 'GENERAL', mediaType: 'SOFTWARE', client: 'ClientB' }
 *     -> only ClientB within that pair; other clients keep bundling
 *   { subject: 'GENERAL', mediaType: 'SOFTWARE', client: 'ClientA', translationSkill: 'MTPE' }
 *     -> only ClientA's Full MTPE offers in that pair
 *
 * Both fields are easy to transpose: `GENERAL` is a subject and `SOFTWARE` a media type, and a row
 * with them the wrong way round matches nothing rather than failing.
 *
 * `client` is the bare client name. The offer carries it as a `web:clientName:` URI, and the
 * prefix is stripped from both sides before comparing, so a row written either way still matches.
 * `translationSkill` is a backend TranslationSkill enum name (Full MTPE is `MTPE`, not `FMTPE`).
 * Due date is deliberately not a dimension, even though it is in the grouping key: a row keyed on
 * a date would silently stop applying.
 *
 * Exclusions are not gated by `offerBundlingExpansion` -- a client must be able to opt out whether
 * or not the expansion is on. They apply to bundling only and never to the "Open All" gate, which
 * is a convenience unrelated to whether offers group. Empty list means nothing is excluded.
 */
export const BUNDLING_EXCLUSIONS = Object.freeze([]);

const CLIENT_URI_PREFIX = 'web:clientName:';

const clientNameOf = clientUri => {
  if (clientUri == null) {
    return null;
  }
  return clientUri.startsWith(CLIENT_URI_PREFIX)
    ? clientUri.slice(CLIENT_URI_PREFIX.length)
    : clientUri;
};

// A row field constrains the offer only where the row names it. Null on the row means "any", so a
// null on the offer can only satisfy a field the row leaves open.
const fieldMatches = (rowValue, offerValue) => rowValue == null || rowValue === offerValue;

// A row must name the pair it carves out of. Enforced here rather than left to the row-hygiene
// test, so a malformed row matches nothing instead of wildcarding both fields and excluding every
// offer -- and so the invariant still holds for rows this is called with directly.
const rowExcludes = (row, offer) =>
  row.subject != null &&
  row.mediaType != null &&
  fieldMatches(row.subject, offer.subject) &&
  fieldMatches(row.mediaType, offer.mediaType) &&
  fieldMatches(clientNameOf(row.client), clientNameOf(offer.clientUri)) &&
  fieldMatches(row.translationSkill, offer.translationSkill) &&
  fieldMatches(row.sourceLocale, offer.sourceLocale) &&
  fieldMatches(row.targetLocale, offer.targetLocale);

/**
 * Whether any exclusion row carves this offer out of bundling.
 *
 * @param {object} offer
 * @param {ReadonlyArray<object>} exclusions rows to match against; defaults to the configured list
 */
export const isOfferExcludedFromBundling = (offer, exclusions = BUNDLING_EXCLUSIONS) => {
  if (offer == null) {
    return false;
  }
  return exclusions.some(row => rowExcludes(row, offer));
};

/**
 * Whether bundling is configured to apply to this offer: its pair is in the effective allowlist and
 * no exclusion row carves it out.
 *
 * This answers only the configuration question. The structural bundling conditions -- the per-offer
 * work-unit limit and the exclusive-offer exclusion -- stay with the caller and are not checked
 * here.
 *
 * @param {{subject: string, mediaType: string}} offer
 * @param {boolean} expansionEnabled resolved value of the `offerBundlingExpansion` flag
 * @param {ReadonlyArray<object>} exclusions rows to match against; defaults to the configured list
 */
export const isOfferAllowedToBundle = (
  offer,
  expansionEnabled,
  exclusions = BUNDLING_EXCLUSIONS
) => {
  if (offer == null) {
    return false;
  }
  return (
    isPairAllowed(offer.subject, offer.mediaType, expansionEnabled) &&
    !isOfferExcludedFromBundling(offer, exclusions)
  );
};
